import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineX } from "react-icons/hi";
import {
  HiOutlineUsers,
  HiOutlineCheckCircle,
  HiOutlineBuildingOffice,
} from "react-icons/hi2";
import { IoIosArrowDown } from "react-icons/io";
import { MdOutlineBusinessCenter } from "react-icons/md";
import {
  Assessment,
  AssessmentStatus,
  OrganizationAssessment,
  Responder,
  Responding,
} from "types/assessmentTypes";

type TabType = "company" | "organization";

type SidebarProps = {
  companyAssessments: Assessment[];
  organizationAssessments: OrganizationAssessment[];
  hasOrganizationAssessments: boolean;
  selectedAssessmentId: number | null;
  selectedResponding: Responding | null;
  selectedOrgAssessment: OrganizationAssessment | null;
  onSelectResponding: (
    assessmentId: number,
    responding: Responding,
    type?: TabType
  ) => void;
  onSelectOrganizationAssessment: (assessment: OrganizationAssessment) => void;
  isMobileOpen: boolean;
  onCloseMobile: () => void;
  assessmentType: TabType;
  isSidebarLoading?: boolean;
  responder?: Responder;
  hasMoreCompany?: boolean;
  hasMoreOrg?: boolean;
  isFetchingCompany?: boolean;
  isFetchingOrg?: boolean;
  onLoadMore?: () => void;
};

export default function Sidebar({
  companyAssessments,
  organizationAssessments,
  hasOrganizationAssessments,
  selectedAssessmentId,
  selectedResponding,
  onSelectResponding,
  onSelectOrganizationAssessment,
  isMobileOpen,
  onCloseMobile,
  assessmentType,
  isSidebarLoading,
  responder,
  hasMoreCompany,
  hasMoreOrg,
  isFetchingCompany,
  isFetchingOrg,
  onLoadMore,
}: SidebarProps) {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loaderRef = useRef<HTMLDivElement>(null);

  // Determine which tabs to show
  const showCompanyTab = companyAssessments.length > 0;
  const showOrgTab =
    hasOrganizationAssessments && organizationAssessments.length > 0;
  const hasBothTabs = showCompanyTab && showOrgTab;

  // Set initial active tab based on available tabs
  const getInitialTab = (): TabType => {
    if (hasBothTabs) return "company";
    if (showCompanyTab) return "company";
    if (showOrgTab) return "organization";
    return "company";
  };

  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab);

  // Update active tab when assessment type changes from parent
  useEffect(() => {
    if (assessmentType === "company" && showCompanyTab) {
      setActiveTab("company");
    } else if (assessmentType === "organization" && showOrgTab) {
      setActiveTab("organization");
    } else {
      if (showCompanyTab) {
        setActiveTab("company");
      } else if (showOrgTab) {
        setActiveTab("organization");
      }
    }
  }, [assessmentType, showCompanyTab, showOrgTab]);

  const [expandedCompanies, setExpandedCompanies] = useState<Set<number>>(
    () => {
      const firstAssessment = companyAssessments[0];
      return firstAssessment
        ? new Set([firstAssessment.id])
        : new Set<number>();
    }
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    const sentinel = loaderRef.current;
    if (!container || !sentinel) return;

    const isFetching =
      assessmentType === "company" ? isFetchingCompany : isFetchingOrg;
    const hasMore = assessmentType === "company" ? hasMoreCompany : hasMoreOrg;

    if (!hasMore || isFetching) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onLoadMore?.();
        }
      },
      {
        root: container,
        rootMargin: "0px 0px 200px 0px",
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    assessmentType,
    companyAssessments.length,
    hasMoreCompany,
    hasMoreOrg,
    isFetchingCompany,
    isFetchingOrg,
    onLoadMore,
  ]);

  // Update expanded companies when companyAssessments changes
  const expandedInitializedRef = useRef(false);

  useEffect(() => {
    if (!expandedInitializedRef.current && companyAssessments.length > 0) {
      setExpandedCompanies(new Set([companyAssessments[0].id]));
      expandedInitializedRef.current = true;
    }
  }, [companyAssessments]);

  const toggleCompany = (companyId: number) => {
    setExpandedCompanies((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(companyId)) {
        newSet.delete(companyId);
      } else {
        newSet.add(companyId);
      }
      return newSet;
    });
  };

  // Get available tabs
  const availableTabs = [];
  if (showCompanyTab) {
    availableTabs.push({
      id: "company" as TabType,
      label: t("assessment.companyAssessments"),
      icon: MdOutlineBusinessCenter,
      count: companyAssessments.length,
      color: "brand",
    });
  }
  if (showOrgTab) {
    availableTabs.push({
      id: "organization" as TabType,
      label: t("assessment.organizationAssessments"),
      icon: HiOutlineBuildingOffice,
      count: organizationAssessments.length,
      color: "purple",
    });
  }

  const shouldShowTabs = availableTabs.length > 1;

  const sidebarContent = (
    <div className="flex h-full flex-col bg-gradient-to-b from-white to-gray-50 dark:from-navy-800 dark:to-navy-900">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200/60 px-5 py-5 dark:border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">
              {t("assessment.evaluations")}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t("assessment.selectGroup")}
            </p>
          </div>
          {isMobileOpen && (
            <button
              onClick={onCloseMobile}
              className="rounded-full p-2 transition-colors hover:bg-gray-100 dark:hover:bg-white/10 lg:hidden"
            >
              <HiOutlineX className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      {shouldShowTabs && (
        <div className="flex-shrink-0 border-b border-gray-200/60 px-2 dark:border-white/10">
          <div className="flex gap-1 rounded-xl bg-gray-100/80 p-1 dark:bg-white/5">
            {availableTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    relative flex flex-1 items-center justify-center gap-2 rounded-lg p-2 text-xs font-medium transition-all duration-300
                    ${
                      isActive
                        ? "bg-white text-gray-900 shadow-lg shadow-gray-200/50 dark:bg-navy-700 dark:text-white dark:shadow-navy-900/50"
                        : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                    }
                  `}
                >
                  <Icon
                    className={`h-4 w-4 ${
                      isActive ? `text-${tab.color}-500` : ""
                    }`}
                  />
                  <span className="whitespace-nowrap">{tab.label}</span>
                  <span
                    className={`
                      ml-1 flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-bold
                      ${
                        isActive
                          ? `bg-${tab.color}-100 text-${tab.color}-600 dark:bg-${tab.color}-500/20 dark:text-${tab.color}-400`
                          : "bg-gray-200 text-gray-600 dark:bg-white/10 dark:text-gray-400"
                      }
                    `}
                  >
                    {tab.count}
                  </span>
                  {isActive && (
                    <motion.span
                      layoutId="sidebarTabIndicator"
                      className={`absolute bottom-0 left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-full bg-${tab.color}-500 dark:bg-${tab.color}-400`}
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 30,
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Content */}
      <div
        ref={scrollContainerRef}
        className="hide-scrollbar min-h-[250px] flex-1 overflow-y-auto p-2"
      >
        <AnimatePresence mode="wait">
          {isSidebarLoading && (
            <div className="flex h-10 items-center justify-center">
              <div className="loader w-5 text-brand-500" />
            </div>
          )}

          {!shouldShowTabs && activeTab === "company" && showCompanyTab && (
            <div className="space-y-2">{renderCompanyAssessments()}</div>
          )}

          {!shouldShowTabs && activeTab === "organization" && showOrgTab && (
            <div className="space-y-2">{renderOrganizationAssessments()}</div>
          )}

          {shouldShowTabs && activeTab === "company" && showCompanyTab && (
            <motion.div
              key="company-tab"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-2"
            >
              {renderCompanyAssessments()}
            </motion.div>
          )}

          {shouldShowTabs && activeTab === "organization" && showOrgTab && (
            <motion.div
              key="organization-tab"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-2"
            >
              {renderOrganizationAssessments()}
            </motion.div>
          )}
        </AnimatePresence>

        {(assessmentType === "company" ? hasMoreCompany : hasMoreOrg) && (
          <div
            ref={loaderRef}
            className="flex h-10 items-center justify-center"
          >
            <div className="h-2.5 w-2.5 rounded-full bg-brand-500" />
          </div>
        )}
      </div>
    </div>
  );

  function renderCompanyAssessments() {
    return (
      <AnimatePresence>
        {companyAssessments.map((assessment, idx) => {
          const isExpanded = expandedCompanies.has(assessment.id);
          const hasResponding =
            (assessment.company?.responding?.length || 0) > 0;
          const answeredCount =
            assessment.company?.responding?.filter((r) => r.isAnswered)
              .length || 0;
          const totalCount = assessment.company?.responding?.length || 0;

          const isActiveAssessment =
            assessmentType === "company" &&
            selectedAssessmentId === assessment.id;

          return (
            <motion.div
              key={`company-${assessment.id}-${idx}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(idx * 0.05, 0.3) }}
              className={`
                group rounded-2xl border transition-all duration-300 hover:shadow-md
                ${
                  isActiveAssessment
                    ? "border-brand-300 bg-gradient-to-r from-brand-50/50 to-white shadow-lg ring-1 ring-brand-200 dark:border-brand-500/50 dark:from-brand-500/10 dark:to-navy-800/50 dark:ring-brand-500/30"
                    : "border-gray-200/60 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800/50"
                }
              `}
            >
              {/* Company Header */}
              <button
                onClick={() => toggleCompany(assessment.id)}
                className="flex w-full items-center justify-between p-4 transition-colors hover:bg-gray-50/50 dark:hover:bg-white/5"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 text-white shadow-lg shadow-brand-500/25">
                    <MdOutlineBusinessCenter className="h-5 w-5" />
                  </div>
                  <div className="text-start">
                    <h3 className="font-semibold text-gray-800 dark:text-white">
                      {assessment.company?.name}
                    </h3>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <HiOutlineUsers className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {totalCount} {t("assessment.groups")}
                        </span>
                      </div>
                      {answeredCount === totalCount && totalCount > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700 dark:bg-green-500/20 dark:text-green-400">
                          <HiOutlineCheckCircle className="h-3 w-3" />
                          {t("assessment.isCompleted")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <motion.div
                  animate={{ rotate: isExpanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <IoIosArrowDown className="h-5 w-5 text-gray-400" />
                </motion.div>
              </button>

              {/* Groups List */}
              <AnimatePresence>
                {isExpanded && hasResponding && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden border-t border-gray-100 dark:border-white/5"
                  >
                    <div className="space-y-1 p-2">
                      {assessment.company?.responding?.map(
                        (responding, rIdx) => {
                          const isActive =
                            assessmentType === "company" &&
                            selectedAssessmentId === assessment.id &&
                            selectedResponding?.groupId === responding.groupId;
                          const isCompleted = responding.isAnswered;

                          return (
                            <motion.button
                              key={`group-${assessment.id}-${responding.groupId}-${rIdx}`}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{
                                delay: Math.min(rIdx * 0.03, 0.2),
                              }}
                              onClick={() =>
                                onSelectResponding(
                                  assessment.id,
                                  responding,
                                  "company"
                                )
                              }
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              className={`
                                relative w-full rounded-xl px-3 py-2.5 text-start transition-all duration-200
                                ${
                                  isActive
                                    ? "bg-gradient-to-r from-brand-50 to-brand-100 text-brand-700 shadow-md dark:from-brand-500/20 dark:to-brand-500/10 dark:text-white"
                                    : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/5"
                                }
                                ${isCompleted ? "opacity-75" : ""}
                              `}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  {isCompleted && (
                                    <HiOutlineCheckCircle className="h-4 w-4 text-green-500" />
                                  )}
                                  <span className="text-sm font-medium">
                                    {t(
                                      `assessmentGroups.${responding.group?.key}`
                                    )}
                                  </span>
                                  {isCompleted && (
                                    <div className="flex items-center gap-1.5 rounded-full bg-green-50 px-2 py-0.5 dark:bg-green-500/20">
                                      <HiOutlineCheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                                      <span className="text-xs font-medium text-green-700 dark:text-green-300">
                                        {t("assessment.completed")}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Active Indicator */}
                              {isActive && (
                                <motion.div
                                  layoutId={`active-indicator-${assessment.id}-${responding.groupId}`}
                                  initial={{ scaleY: 0, opacity: 0 }}
                                  animate={{ scaleY: 1, opacity: 1 }}
                                  exit={{ scaleY: 0, opacity: 0 }}
                                  transition={{
                                    duration: 0.2,
                                    ease: "easeInOut",
                                  }}
                                  className="absolute inset-y-2 right-0 w-1 rounded-full bg-gradient-to-b from-brand-500 to-brand-600"
                                />
                              )}
                            </motion.button>
                          );
                        }
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}

        {companyAssessments.length === 0 && (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("assessment.noCompanyAssessments")}
            </p>
          </div>
        )}
      </AnimatePresence>
    );
  }

  function renderOrganizationAssessments() {
    return (
      <AnimatePresence>
        {organizationAssessments.map((assessment, idx) => {
          const isActiveAssessment =
            assessmentType === "organization" &&
            selectedAssessmentId === assessment.id;

          const hasAnswers =
            assessment.status === AssessmentStatus.COMPLETED ||
            assessment.answers?.some(
              (answer) => answer.responding?.responder?.id === responder?.id
            );

          return (
            <motion.button
              key={`org-${assessment.id}-${idx}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(idx * 0.05, 0.3) }}
              onClick={() => onSelectOrganizationAssessment(assessment)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`
                relative w-full rounded-2xl border p-4 text-start transition-all duration-300 hover:shadow-md
                ${
                  isActiveAssessment
                    ? "border-purple-300 bg-gradient-to-r from-purple-50/50 to-white shadow-lg ring-1 ring-purple-200 dark:border-purple-500/50 dark:from-purple-500/10 dark:to-navy-800/50 dark:ring-purple-500/30"
                    : "border-gray-200/60 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800/50"
                }
              `}
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-lg shadow-purple-500/25">
                  <HiOutlineBuildingOffice className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-800 dark:text-white">
                    {assessment.company?.name}
                  </h3>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {assessment.organization?.name +
                        " " +
                        assessment.organization?.province?.name || ""}
                    </span>
                    {hasAnswers && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700 dark:bg-green-500/20 dark:text-green-400">
                        <HiOutlineCheckCircle className="h-3 w-3" />
                        {t("assessment.completed")}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Active Indicator */}
              {isActiveAssessment && (
                <motion.div
                  layoutId={`active-indicator-org-${assessment.id}`}
                  initial={{ scaleY: 0, opacity: 0 }}
                  animate={{ scaleY: 1, opacity: 1 }}
                  exit={{ scaleY: 0, opacity: 0 }}
                  transition={{
                    duration: 0.2,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-y-3 start-0 w-1 rounded-full bg-gradient-to-b from-purple-500 to-purple-600"
                />
              )}
            </motion.button>
          );
        })}

        {organizationAssessments.length === 0 && (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("assessment.noOrganizationAssessments")}
            </p>
          </div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden h-full w-[380px] flex-shrink-0 lg:block">
        <div className="h-full overflow-hidden rounded-2xl border border-gray-200/60 bg-white shadow-xl dark:border-white/10 dark:bg-navy-800/50">
          {sidebarContent}
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isMobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-black/50 fixed inset-0 z-50 backdrop-blur-md lg:hidden"
              onClick={onCloseMobile}
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed inset-y-0 left-0 z-50 w-[85%] max-w-[380px] shadow-2xl lg:hidden"
            >
              <div className="h-full overflow-hidden rounded-r-2xl border-r border-gray-200/60 bg-white dark:border-white/10 dark:bg-navy-800">
                {sidebarContent}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
